import { Backdrop, Box, Typography, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { tokens } from "../theme/theme";

// Fullscreen overlay shown while an optimization request is running.
// Construction-site style: three "crews" build the network in parallel —
// for every pipe a dashed trench marking appears first, then a little
// worker (wrench/hammer/pickaxe) hammers its way along the edge while
// the pipe draws in behind it; nodes pop up with a spark burst. Once the
// network is complete, flow runs along the pipes towards the outfall,
// the pipe widths "breathe" (as if being optimized) and the nodes pulse.
// Below it rotating status messages.

// Cycled status messages (i18n keys).
const MESSAGES = [
  "Building network...",
  "Analyzing network...",
  "Optimizing pipe diameters...",
  "Calculating flow velocities...",
  "Checking constraints...",
  "Minimizing costs...",
];

// Demo network: sources on the outside collecting via three branches
// (top left, top right, bottom left) into a trunk towards the outfall
// (bottom right). `delay` = when the node pops up during the build-up.
const NODES = [
  { x: 20, y: 18, delay: 0.05 }, // 0 source
  { x: 75, y: 12, delay: 0.6 }, // 1 source
  { x: 50, y: 55, delay: 0.75 }, // 2 junction (0,1)
  { x: 120, y: 30, delay: 1.15 }, // 3 source
  { x: 100, y: 95, delay: 1.85 }, // 4 junction (2,3,5)
  { x: 30, y: 110, delay: 2.25 }, // 5 source
  { x: 180, y: 15, delay: 0.2 }, // 6 source
  { x: 240, y: 28, delay: 0.75 }, // 7 source
  { x: 210, y: 70, delay: 0.9 }, // 8 junction (6,7)
  { x: 300, y: 12, delay: 1.3 }, // 9 source
  { x: 350, y: 40, delay: 1.85 }, // 10 source
  { x: 325, y: 85, delay: 2.0 }, // 11 junction (9,10)
  { x: 265, y: 125, delay: 3.1 }, // 12 junction (8,11)
  { x: 20, y: 190, delay: 0.35 }, // 13 source
  { x: 80, y: 230, delay: 1.05 }, // 14 junction (13,15)
  { x: 45, y: 275, delay: 0.9 }, // 15 source
  { x: 150, y: 255, delay: 2.15 }, // 16 junction (14)
  { x: 190, y: 185, delay: 2.7 }, // 17 trunk junction (4,16)
  { x: 300, y: 210, delay: 3.25 }, // 18 trunk junction (17,12)
  { x: 390, y: 130, delay: 3.1 }, // 19 source
];
const OUTFALL = { x: 430, y: 270, delay: 3.8 };

// Edges in flow direction (from → to). Three crews build in parallel,
// `delay` = when construction of this pipe starts.
const EDGES = [
  // crew 1: top-left branch, then the trunk connection of branch A
  { from: NODES[0], to: NODES[2], delay: 0.2 },
  { from: NODES[1], to: NODES[2], delay: 0.75 },
  { from: NODES[3], to: NODES[4], delay: 1.3 },
  { from: NODES[2], to: NODES[4], delay: 1.85 },
  { from: NODES[5], to: NODES[4], delay: 2.4 },
  { from: NODES[4], to: NODES[17], delay: 2.95 },
  // crew 2: top-right branch
  { from: NODES[6], to: NODES[8], delay: 0.35 },
  { from: NODES[7], to: NODES[8], delay: 0.9 },
  { from: NODES[9], to: NODES[11], delay: 1.45 },
  { from: NODES[10], to: NODES[11], delay: 2.0 },
  { from: NODES[8], to: NODES[12], delay: 2.55 },
  { from: NODES[11], to: NODES[12], delay: 3.1 },
  { from: NODES[12], to: NODES[18], delay: 3.65 },
  // crew 3: bottom-left branch + trunk to the outfall
  { from: NODES[13], to: NODES[14], delay: 0.5 },
  { from: NODES[15], to: NODES[14], delay: 1.05 },
  { from: NODES[14], to: NODES[16], delay: 1.6 },
  { from: NODES[16], to: NODES[17], delay: 2.15 },
  { from: NODES[17], to: NODES[18], delay: 2.7 },
  { from: NODES[19], to: OUTFALL, delay: 3.25 },
  { from: NODES[18], to: OUTFALL, delay: 3.8 },
];

const DRAW_DURATION = 0.55; // s a single pipe takes to build
const BUILD_END =
  Math.max(...EDGES.map((edge) => edge.delay)) + DRAW_DURATION;
const FLOW_START = BUILD_END + 0.3;

// Time (ms) one full build-up takes: every pipe drawn plus a short moment of
// flow running afterwards. Callers use this to keep the overlay visible for at
// least one complete animation cycle before the optimization result replaces
// it, even when the request itself returns faster.
export const OPTIMIZATION_BUILD_MS = Math.round((FLOW_START + 1) * 1000);

const WORKER_TOOLS = ["🔧", "🔨", "⛏️"];

const edgeLength = (edge) =>
  Math.hypot(edge.to.x - edge.from.x, edge.to.y - edge.from.y);

// Spark burst around a point: 6 short rays scaling out and fading.
const SPARK_ANGLES = [0, 60, 120, 180, 240, 300];
const Sparks = ({ x, y, delay }) => (
  <g
    className="opti-sparks"
    style={{ animation: `optiSpark 0.5s ease-out ${delay}s both` }}
  >
    {SPARK_ANGLES.map((angle) => {
      const rad = (angle * Math.PI) / 180;
      return (
        <line
          key={angle}
          x1={x + Math.cos(rad) * 8}
          y1={y + Math.sin(rad) * 8}
          x2={x + Math.cos(rad) * 13}
          y2={y + Math.sin(rad) * 13}
        />
      );
    })}
  </g>
);

export const OptimizationOverlay = ({ active }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  // rotate through the status messages while active
  const [messageIndex, setMessageIndex] = useState(0);
  useEffect(() => {
    if (!active) return;
    setMessageIndex(0);
    const interval = setInterval(
      () => setMessageIndex((i) => (i + 1) % MESSAGES.length),
      2600
    );
    return () => clearInterval(interval);
  }, [active]);

  const pipeColor = "hsl(210, 85%, 45%)";
  const flowColor = "hsl(210, 90%, 75%)";
  const nodeColor = "hsl(210, 85%, 35%)";
  const outfallColor = "hsl(150, 70%, 45%)";
  const sparkColor = "hsl(40, 95%, 55%)";
  const trenchColor = theme.palette.mode === "dark" ? "#999" : "#777";

  return (
    <Backdrop
      open={Boolean(active)}
      sx={{
        zIndex: 99999999,
        bgcolor: colors.contrast[200] + "80",
        backdropFilter: "blur(10px)",
      }}
    >
      {Boolean(active) && (
        <Box
          className="flex flex-col items-center gap-5"
          sx={{
            // trench marking: dashed line appearing before the pipe is built
            "& .opti-trench": {
              stroke: trenchColor,
              strokeWidth: 1.5,
              strokeDasharray: "4 4",
              opacity: 0,
            },
            // pipes: draw in during the build-up, then the width
            // "breathes" as if diameters are being optimized
            "& .opti-pipe": {
              stroke: pipeColor,
              strokeWidth: 3,
              strokeLinecap: "round",
            },
            // flow: dashes running along the pipe in flow direction,
            // fading in once the network is complete
            "& .opti-flow": {
              stroke: flowColor,
              strokeWidth: 2,
              strokeLinecap: "round",
              strokeDasharray: "6 10",
              opacity: 0,
            },
            // nodes: pop up during the build-up, then pulse gently
            "& .opti-node": {
              fill: nodeColor,
              opacity: 0,
              transformBox: "fill-box",
              transformOrigin: "center",
            },
            // spark burst when a node pops up
            "& .opti-sparks": {
              stroke: sparkColor,
              strokeWidth: 2,
              strokeLinecap: "round",
              opacity: 0,
              transformBox: "fill-box",
              transformOrigin: "center",
            },
            // little worker travelling along the pipe while it is built
            "& .opti-worker": {
              opacity: 0,
            },
            "& .opti-tool": {
              transformBox: "fill-box",
              transformOrigin: "50% 80%",
              animation: "optiSwing 0.16s ease-in-out infinite alternate",
            },
            // outfall: expanding fading ring (after the build-up)
            "& .opti-ring": {
              fill: "none",
              stroke: outfallColor,
              strokeWidth: 2,
              opacity: 0,
              transformBox: "fill-box",
              transformOrigin: "center",
              animation: `optiRing 1.8s ease-out ${FLOW_START}s infinite`,
            },
            "& .opti-outfall": {
              fill: outfallColor,
              opacity: 0,
              transformBox: "fill-box",
              transformOrigin: "center",
            },
            "@keyframes optiTrench": {
              "0%": { opacity: 0 },
              "20%": { opacity: 0.7 },
              "75%": { opacity: 0.7 },
              "100%": { opacity: 0 },
            },
            "@keyframes optiDraw": {
              to: { strokeDashoffset: 0 },
            },
            "@keyframes optiPipe": {
              "0%, 100%": { strokeWidth: 3 },
              "50%": { strokeWidth: 7 },
            },
            "@keyframes optiFlow": {
              to: { strokeDashoffset: -16 },
            },
            "@keyframes optiAppear": {
              to: { opacity: 1 },
            },
            "@keyframes optiPop": {
              "0%": { transform: "scale(0)", opacity: 0 },
              "60%": { transform: "scale(1.5)", opacity: 1 },
              "100%": { transform: "scale(1)", opacity: 1 },
            },
            "@keyframes optiNode": {
              "0%, 100%": { transform: "scale(1)" },
              "50%": { transform: "scale(1.35)" },
            },
            "@keyframes optiSpark": {
              "0%": { transform: "scale(0.3)", opacity: 0 },
              "30%": { opacity: 1 },
              "100%": { transform: "scale(1.6)", opacity: 0 },
            },
            "@keyframes optiWork": {
              "0%": {
                transform: "translate(var(--fx), var(--fy))",
                opacity: 0,
              },
              "12%": { opacity: 1 },
              "88%": { opacity: 1 },
              "100%": {
                transform: "translate(var(--tx), var(--ty))",
                opacity: 0,
              },
            },
            "@keyframes optiSwing": {
              from: { transform: "rotate(-22deg)" },
              to: { transform: "rotate(26deg)" },
            },
            "@keyframes optiRing": {
              "0%": { transform: "scale(0.4)", opacity: 1 },
              "100%": { transform: "scale(2.2)", opacity: 0 },
            },
            "@keyframes optiFadeIn": {
              from: { opacity: 0, transform: "translateY(6px)" },
              to: { opacity: 1, transform: "translateY(0)" },
            },
          }}
        >
          <svg
            width="480"
            height="310"
            viewBox="0 0 450 290"
            style={{ overflow: "visible", maxWidth: "90vw" }}
          >
            {/* trench markings (dashed, appear before the pipe is built) */}
            {EDGES.map((edge, i) => (
              <line
                key={i}
                className="opti-trench"
                x1={edge.from.x}
                y1={edge.from.y}
                x2={edge.to.x}
                y2={edge.to.y}
                style={{
                  animation: `optiTrench ${DRAW_DURATION + 0.6}s linear ${
                    Math.max(edge.delay - 0.3, 0)
                  }s both`,
                }}
              />
            ))}
            {/* pipes + flow */}
            {EDGES.map((edge, i) => {
              const length = edgeLength(edge);
              return (
                <g key={i}>
                  <line
                    className="opti-pipe"
                    x1={edge.from.x}
                    y1={edge.from.y}
                    x2={edge.to.x}
                    y2={edge.to.y}
                    style={{
                      strokeDasharray: length,
                      strokeDashoffset: length,
                      animation:
                        `optiDraw ${DRAW_DURATION}s linear ${edge.delay}s forwards, ` +
                        `optiPipe 2.6s ease-in-out ${
                          FLOW_START + i * 0.15
                        }s infinite`,
                    }}
                  />
                  <line
                    className="opti-flow"
                    x1={edge.from.x}
                    y1={edge.from.y}
                    x2={edge.to.x}
                    y2={edge.to.y}
                    style={{
                      animation:
                        `optiAppear 0.4s ease-out ${FLOW_START}s forwards, ` +
                        `optiFlow 1.1s linear ${
                          FLOW_START - i * 0.35
                        }s infinite`,
                    }}
                  />
                </g>
              );
            })}
            {/* spark bursts when nodes pop up */}
            {NODES.map((node, i) => (
              <Sparks key={i} x={node.x} y={node.y} delay={node.delay + 0.1} />
            ))}
            <Sparks x={OUTFALL.x} y={OUTFALL.y} delay={OUTFALL.delay + 0.1} />
            {/* nodes */}
            {NODES.map((node, i) => (
              <circle
                key={i}
                className="opti-node"
                cx={node.x}
                cy={node.y}
                r={5}
                style={{
                  animation:
                    `optiPop 0.4s ease-out ${node.delay}s forwards, ` +
                    `optiNode 2.6s ease-in-out ${
                      FLOW_START + i * 0.2
                    }s infinite`,
                }}
              />
            ))}
            <circle
              className="opti-ring"
              cx={OUTFALL.x}
              cy={OUTFALL.y}
              r={7}
            />
            <circle
              className="opti-outfall"
              cx={OUTFALL.x}
              cy={OUTFALL.y}
              r={6}
              style={{
                animation: `optiPop 0.5s ease-out ${OUTFALL.delay}s forwards`,
              }}
            />
            {/* workers hammering their way along each pipe while it builds */}
            {EDGES.map((edge, i) => (
              <g
                key={i}
                className="opti-worker"
                style={{
                  "--fx": `${edge.from.x}px`,
                  "--fy": `${edge.from.y - 9}px`,
                  "--tx": `${edge.to.x}px`,
                  "--ty": `${edge.to.y - 9}px`,
                  animation: `optiWork ${DRAW_DURATION}s linear ${edge.delay}s both`,
                }}
              >
                <text
                  className="opti-tool"
                  textAnchor="middle"
                  fontSize="14"
                  style={{ userSelect: "none" }}
                >
                  {WORKER_TOOLS[i % WORKER_TOOLS.length]}
                </text>
              </g>
            ))}
          </svg>
          <Typography variant="h3">{t("Optimization running")}</Typography>
          <Typography
            key={messageIndex}
            variant="h5"
            sx={{ opacity: 0.8, animation: "optiFadeIn 0.5s ease-out" }}
          >
            {t(MESSAGES[messageIndex])}
          </Typography>
        </Box>
      )}
    </Backdrop>
  );
};

export default OptimizationOverlay;
